<template>
  <v-dialog :value="value" fullscreen hide-overlay transition="fade-transition" @input="onDialogInput">
    <div class="select-product-image-dialog" @click.self="cancel">
      <div class="select-product-image-dialog__panel" role="dialog" :aria-labelledby="titleId" @click.stop>
        <div class="select-product-image-dialog__topbar">
          <h2 :id="titleId" class="select-product-image-dialog__title">Select product image</h2>
          <v-btn icon title="Close" @click="cancel">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <div v-if="productImages.length && productImages.length > 0">
          <p class="select-product-image-dialog__subtitle">
            Supported image formats: PNG, JPG, JPEG, and WebP.
          </p>
          <div class="select-product-image-dialog__grid" role="list" aria-label="Product images">
            <div
              v-for="(item, idx) in productImages"
              :key="item.id || idx"
              class="select-product-image-dialog__tile"
              role="listitem"
            >
              <button
                type="button"
                class="select-product-image-dialog__thumb"
                :aria-label="`Select image ${idx + 1}`"
                @click="selectImage(item)"
              >
                <img
                  class="select-product-image-dialog__thumb-media"
                  :src="item.thumbUrl"
                  :alt="`Product image ${idx + 1}`"
                  loading="lazy"
                />
                <span class="select-product-image-dialog__thumb-overlay" aria-hidden="true">
                  <v-icon color="white" size="24">
                    mdi-check-circle-outline
                  </v-icon>
                </span>
              </button>
              <button
                type="button"
                class="select-product-image-dialog__preview-btn"
                aria-label="Preview image"
                title="Preview image"
                @click.stop="openPreview(item.fullUrl)"
              >
                <v-icon size="20" color="#1f1f1f">mdi-magnify-plus-outline</v-icon>
              </button>
            </div>
          </div>
        </div>

        <p v-else class="select-product-image-dialog__empty">No product images available.</p>

        <div class="select-product-image-dialog__actions">
          <v-btn text class="select-product-image-dialog__action-btn" @click="cancel">Cancel</v-btn>
        </div>
      </div>
    </div>

    <PreviewImage v-if="previewImage" :src="previewImage" :show-preview="showPreview" @close="closePreview" />
  </v-dialog>
</template>

<script>
import imageHelpers from '@/helpers/image';
import PreviewImage from './PreviewImage.vue';
import { isAllowedImageUrl } from './utils';

const THUMB_SIZE = 100;
const SELECT_SIZE = 980;

export default {
  name: 'SelectProductImage',
  components: { PreviewImage },
  props: {
    value: { type: Boolean, default: false },
    product: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      showPreview: false,
      previewImage: null,
      titleId: 'select-product-image-title',
    };
  },

  computed: {
    productImages() {
      const images = Array.isArray(this.product?.images) ? this.product.images : [];
      return images
        .filter(image => image && image.src)
        .map((image, index) => ({
          id: image.id || image._id || `product-image-${index}`,
          thumbUrl: imageHelpers.url(image.src, { width: THUMB_SIZE, height: THUMB_SIZE }),
          fullUrl: imageHelpers.url(image.src, { width: SELECT_SIZE, height: SELECT_SIZE }),
        }));
    },
  },

  watch: {
    value(open) {
      if (!open) this.closePreview();
    },
  },

  methods: {
    onDialogInput(val) {
      this.$emit('input', !!val);
      if (!val) this.closePreview();
    },

    cancel() {
      this.$emit('input', false);
      this.closePreview();
    },

    notifyError(message) {
      this.$store.commit('setMessages', {
        type: 'error',
        messages: message,
      });
    },

    selectImage(item) {
      const url = String(item?.fullUrl || '').trim();
      if (!url) return;
      if (!isAllowedImageUrl(url)) {
        this.notifyError('Only PNG, JPG, JPEG, and WebP image URLs are supported.');
        return;
      }
      this.$emit('select', { url });
      this.$emit('input', false);
      this.closePreview();
    },

    openPreview(url) {
      const src = String(url || '').trim();
      if (!src) return;
      this.previewImage = src;
      this.showPreview = true;
    },

    closePreview() {
      this.showPreview = false;
      this.previewImage = null;
    },
  },
};
</script>

<style scoped lang="scss">
.select-product-image-dialog {
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.select-product-image-dialog__panel {
  width: 100%;
  max-width: 880px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  padding: 16px 18px 18px;
  max-height: calc(100vh - 48px);
  overflow: auto;
}

.select-product-image-dialog__topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.select-product-image-dialog__title {
  margin: 4px 0 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--main-text-color, #1f1f1f);
}

.select-product-image-dialog__subtitle {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.55);
}

.select-product-image-dialog__grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.select-product-image-dialog__tile {
  position: relative;
  width: 160px;
  height: 160px;
}

.select-product-image-dialog__thumb {
  position: relative;
  width: 160px;
  height: 160px;
  border-radius: 10px;
  border: 2px solid transparent;
  background: #f6f6f6;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  display: block;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--v-primary-base, #1976d2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

    .select-product-image-dialog__thumb-overlay {
      opacity: 1;
    }
  }
}

.select-product-image-dialog__thumb-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.select-product-image-dialog__thumb-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
}

.select-product-image-dialog__preview-btn {
  position: absolute;
  right: 4px;
  top: 4px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 999px;
  background: #e9eef6;
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;
  z-index: 2;
  opacity: 0;
  transition: opacity 0.15s ease;

  .select-product-image-dialog__tile:hover & {
    opacity: 1;
  }
}

.select-product-image-dialog__empty {
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.55);
  text-align: center;
}

.select-product-image-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
  padding-top: 4px;
}

.select-product-image-dialog__action-btn {
  min-width: 88px !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  background-color: var(--v-primary-base);
  color: #fff;
}
</style>
